"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  GAME_STORAGE_KEY,
  MAX_GUESSES,
  MIN_NAME_LENGTH,
  NAME_STORAGE_KEY,
  TOAST_DURATION_MS,
  WORD_LENGTH,
} from "../_logic/constants";
import { leaderboardDayKey } from "../_logic/day";
import type { GuessResponse, RoundState, ScoredGuess } from "../_logic/types";

/** Everything persisted so a refresh resumes the round instead of restarting it. */
interface StoredRound {
  day: string;
  name: string;
  token: string;
  startedAt: number;
  guesses: ScoredGuess[];
  state: RoundState;
  answer: string | null;
  timeSeconds: number | null;
  counted: boolean;
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch {
    // Private mode / blocked storage: the round still works, it just won't survive a refresh.
  }
}

/**
 * Drives one Wordle round against the server: starting, typing, submitting, and resuming after a refresh.
 * Guesses are scored by `/api/wordle/guess`, so the answer only reaches the browser once the round ends.
 * @returns Round state plus the actions the UI needs.
 */
export function useWordleRound() {
  // States
  const [name, setName] = useState("");
  const [round, setRound] = useState<StoredRound | null>(null);
  const [currentGuess, setCurrentGuess] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived values
  const state: RoundState = round?.state ?? "playing";
  const isPlaying = Boolean(round) && state === "playing";

  // Handlers
  const flash = useCallback((message: string, shake = false) => {
    setToast(message);
    if (shake) setShakeKey((k) => k + 1);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const persist = useCallback((next: StoredRound) => {
    setRound(next);
    writeStored(GAME_STORAGE_KEY, next);
  }, []);

  const start = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length < MIN_NAME_LENGTH) {
      flash("Add your name so we can put you on the leaderboard");
      return;
    }
    setStarting(true);
    try {
      const res = await fetch("/api/wordle/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        flash(data.error ?? "Couldn't start the game");
        return;
      }
      writeStored(NAME_STORAGE_KEY, trimmed);
      persist({
        day: data.day,
        name: trimmed,
        token: data.token,
        startedAt: Date.now(),
        guesses: [],
        state: "playing",
        answer: null,
        timeSeconds: null,
        counted: false,
      });
    } catch {
      flash("Couldn't reach the server. Check your connection.");
    } finally {
      setStarting(false);
    }
  }, [name, flash, persist]);

  const submit = useCallback(async () => {
    if (!round || !isPlaying || submitting) return;
    if (currentGuess.length !== WORD_LENGTH) {
      flash("Not enough letters", true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/wordle/guess", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: round.token, guess: currentGuess }),
      });
      const data = await res.json();
      if (!res.ok) {
        flash(data.error ?? "Something went wrong", res.status === 400);
        return;
      }
      const result = data as GuessResponse;
      persist({
        ...round,
        token: result.token,
        guesses: result.guesses,
        state: result.state,
        answer: result.answer,
        timeSeconds: result.timeSeconds,
        counted: result.counted,
      });
      setCurrentGuess("");
    } catch {
      flash("Couldn't reach the server. Try again.");
    } finally {
      setSubmitting(false);
    }
  }, [round, isPlaying, submitting, currentGuess, flash, persist]);

  const pressKey = useCallback(
    (key: string) => {
      if (!isPlaying || submitting) return;
      if (key === "ENTER") {
        submit();
        return;
      }
      if (key === "BACKSPACE") {
        setCurrentGuess((g) => g.slice(0, -1));
        return;
      }
      if (/^[A-Z]$/.test(key)) setCurrentGuess((g) => (g.length < WORD_LENGTH ? g + key : g));
    },
    [isPlaying, submitting, submit]
  );

  const restartAsSomeoneElse = useCallback(() => {
    try {
      localStorage.removeItem(GAME_STORAGE_KEY);
    } catch {
      // ignore
    }
    setRound(null);
    setCurrentGuess("");
  }, []);

  // Effects
  useEffect(() => {
    const storedName = readStored<string>(NAME_STORAGE_KEY);
    if (storedName) setName(storedName);
    const stored = readStored<StoredRound>(GAME_STORAGE_KEY);
    if (stored?.day === leaderboardDayKey()) setRound(stored);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!round) return;
    if (round.state !== "playing") {
      setElapsed(round.timeSeconds ?? 0);
      return;
    }
    const tick = () => setElapsed(Math.floor((Date.now() - round.startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [round]);

  useEffect(() => {
    if (!isPlaying) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Enter") pressKey("ENTER");
      else if (e.key === "Backspace") pressKey("BACKSPACE");
      else if (/^[a-zA-Z]$/.test(e.key)) pressKey(e.key.toUpperCase());
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPlaying, pressKey]);

  return {
    hydrated,
    name,
    setName,
    round,
    state,
    guesses: round?.guesses ?? [],
    currentGuess,
    remaining: MAX_GUESSES - (round?.guesses.length ?? 0),
    elapsed,
    toast,
    shakeKey,
    submitting,
    starting,
    start,
    pressKey,
    restartAsSomeoneElse,
  };
}
